import { generateSecretKey, getPublicKey, nip19 } from 'nostr-tools';

import { bytesToHex, hexToBytes } from './utils';

const GUEST_KEY_STORAGE = 'ground:guest-key';

let cachedSecretKey: Uint8Array | null = null;

/**
 * Return a stable local secret key for the no-login demo identity.
 *
 * The key is generated once and persisted to localStorage so the same guest
 * identity publishes across reloads. This is a throwaway identity — it is not
 * a substitute for a real Nostr login and should never hold value.
 */
export function getGuestSecretKey(): Uint8Array {
  if (cachedSecretKey) return cachedSecretKey;

  try {
    const stored = localStorage.getItem(GUEST_KEY_STORAGE);
    if (stored) {
      cachedSecretKey = hexToBytes(stored);
      return cachedSecretKey;
    }
  } catch {
    // Ignore storage access errors and fall through to generating a key.
  }

  const secretKey = generateSecretKey();
  cachedSecretKey = secretKey;

  try {
    localStorage.setItem(GUEST_KEY_STORAGE, bytesToHex(secretKey));
  } catch {
    // Best-effort persistence; the in-memory key still works for this session.
  }

  return secretKey;
}

export function getGuestPubkey(): string {
  return getPublicKey(getGuestSecretKey());
}

export function getGuestNpub(): string {
  return nip19.npubEncode(getGuestPubkey());
}
